/**
 * Utility functions for formatting data
 *
 * @module utils/formatters
 */

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale string (default 'es-CR')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, locale = 'es-CR') => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    // Use UTC timezone for date-only strings to avoid day shift from timezone offset
    const isDateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(isDateOnly && { timeZone: 'UTC' }),
    }).format(dateObj);
  } catch (error) {
    console.error('Date formatting error:', error);
    return '-';
  }
};

/**
 * Format date for display in a short/compact form (e.g. "7 jul")
 * Used by kanban/card-style views where space is limited.
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale string (default 'es-CR')
 * @returns {string} Formatted short date string
 */
export const formatDateShort = (date, locale = 'es-CR') => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    // Use UTC timezone for date-only strings to avoid day shift from timezone offset
    const isDateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      ...(isDateOnly && { timeZone: 'UTC' }),
    }).format(dateObj);
  } catch (error) {
    console.error('Date formatting error:', error);
    return '-';
  }
};

/**
 * Parse a value into a Date suitable for calendar-day comparisons
 * (overdue checks, "days until due" math, etc).
 *
 * Date-only "YYYY-MM-DD" strings are anchored to LOCAL midnight instead of
 * being parsed natively (which anchors to UTC midnight). Comparing/mutating
 * a UTC-midnight instant with local-time methods (e.g. `setHours(0,0,0,0)`
 * or comparing against `new Date()`) shifts the effective day back by one
 * in UTC-negative zones (e.g. Costa Rica, UTC-6) -- the same root cause as
 * the due_date display bug fixed via formatDate/formatDateShort (#266).
 * Anchoring to local midnight up front sidesteps that entirely.
 *
 * @param {string|Date} date - Date-only string, timestamp string, or Date
 * @returns {Date|null} Date object, or null for empty/falsy input
 */
export const parseDateOnly = (date) => {
  if (!date) return null;
  if (date instanceof Date) return new Date(date.getTime());

  const isDateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
  if (isDateOnly) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(date);
};

/**
 * Determine whether a due date is overdue relative to today's local
 * calendar day. Never overdue when there is no due date or the item is
 * already completed/cancelled.
 *
 * Uses parseDateOnly so date-only "YYYY-MM-DD" values are compared by
 * calendar day rather than by raw instant, avoiding the timezone day-shift
 * bug (#266) that a naive `new Date(dueDate) < new Date()` comparison hits.
 *
 * @param {string|Date} dueDate - Date-only string, timestamp string, or Date
 * @param {string} [status] - Task/entity status; 'completed'/'cancelled' are never overdue
 * @returns {boolean}
 */
export const isOverdue = (dueDate, status) => {
  if (!dueDate) return false;
  if (status === 'completed' || status === 'cancelled') return false;

  const due = parseDateOnly(dueDate);
  if (!due || Number.isNaN(due.getTime())) return false;

  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return due < today;
};

/**
 * Format date and time for display
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale string (default 'es-CR')
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date, locale = 'es-CR') => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  } catch (error) {
    console.error('DateTime formatting error:', error);
    return '-';
  }
};

/**
 * Edad relativa de un instante ("hoy", "ayer", "hace 5 d", "hace 3 sem").
 *
 * #569 — NO reemplaza a `formatDate`/`formatDateTime`: los acompaña. En una
 * cola FIFO (la bandeja de solicitudes de materiales se ordena por
 * `created_at` ascendente) una fecha DD/MM/YYYY deja indistinguibles todos los
 * ítems del mismo día, que es justo cuando hace falta saber cuál lleva más
 * esperando. La fecha absoluta sigue siendo la que se muestra; esto es el
 * segundo eje.
 *
 * Se compara por DÍA CALENDARIO local, no por diferencia de milisegundos: algo
 * de las 23:50 de ayer tiene 8 horas de antigüedad pero es "ayer", no "hoy".
 *
 * @param {string|Date} date - Timestamp o Date
 * @param {Date} [now] - Instante de referencia (inyectable para tests)
 * @returns {string} Etiqueta relativa, o '' si la fecha no es utilizable
 */
export const formatRelativeAge = (date, now = new Date()) => {
  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dateObj.getTime())) return '';

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(dateObj)) / 86400000);

  if (days < 0) return 'en el futuro';
  if (days === 0) return 'hoy';
  if (days === 1) return 'ayer';
  if (days < 7) return `hace ${days} d`;
  if (days < 30) return `hace ${Math.floor(days / 7)} sem`;
  if (days < 365) return `hace ${Math.floor(days / 30)} m`;
  return `hace ${Math.floor(days / 365)} a`;
};

/**
 * Format time only (HH:MM)
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale string (default 'es-CR')
 * @returns {string} Formatted time string
 */
export const formatTime = (date, locale = 'es-CR') => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(dateObj);
  } catch (error) {
    console.error('Time formatting error:', error);
    return '-';
  }
};

/**
 * Format currency.
 *
 * `currency` NO TIENE DEFAULT, A PROPOSITO (#617). Leer esto antes de "arreglarlo".
 *
 * Hasta #617 el default era `'USD'` con locale `'es-CR'`. Censo por AST: de las
 * 174 llamadas de produccion, 149 no pasaban moneda, y ~145 de esas caian aca.
 * Como ProMedic cotiza en colones, eso rotulaba colones como dolares — en el
 * PDF de factura y en el ClientDashboard, o sea en documentos que ve el cliente,
 * errando por ~500x con formato impecable y sin tirar un solo error.
 *
 * Cambiar el default a `'CRC'` arreglaba las 149 de hoy y dejaba la MISMA trampa
 * armada al reves para la primera venta en dolares, con identica falla
 * silenciosa. Por eso se saco el default en vez de darlo vuelta.
 *
 * REGLA: ningun camino renderiza un simbolo de moneda que no salga de un dato.
 * Solo tres tablas guardan moneda hoy — `invoices`, `purchases` e
 * `inventory_batches`. El resto del modelo (quotations, projects, expenses,
 * tasks, milestones, opportunities) no tiene columna `currency`.
 *
 * QUE PASA SIN `currency`: devuelve el numero SIN simbolo, no tira.
 * Las dos opciones eran fallar ruidosamente o formatear sin simbolo. Tirar es
 * inejecutable: ~145 rutas de render no tienen ningun dato de moneda que pasarle,
 * asi que una excepcion no las arregla — las voltea. Y el objetivo no es tumbar
 * la app, es dejar de mentir. Un monto sin rotulo se VE en pantalla y presiona
 * para que se agregue el dato; la mentira anterior no se veia. En desarrollo
 * ademas avisa por consola, asi que tampoco es silencioso del lado del que
 * programa. La cura de fondo es una columna `currency` en `quotations` para que
 * la factura la herede en vez de asumirla (seguimiento ya anotado en la
 * migracion 20260805120000_l558_invoice_wave_formula.sql).
 *
 * @param {number} amount - Amount to format
 * @param {string} [currency] - ISO 4217 code. Sin esto NO se renderiza simbolo.
 * @param {string} locale - Locale string (default 'es-CR')
 * @param {Object} [options]
 * @param {'symbol'|'code'|'name'} [options.currencyDisplay] - Como rotular.
 *   `'code'` existe para el PDF: jsPDF usa las Type1 estandar con
 *   `/Encoding /WinAnsiEncoding` y sin `ToUnicode`, y `₡` (U+20A1) no esta en
 *   WinAnsi. Al meterlo, jsPDF pasa la cadena ENTERA a dos bytes por caracter y
 *   el visor la lee byte a byte, o sea que no se rompe el simbolo: se rompe el
 *   renglon completo. Con `'code'` sale `CRC 2 804 208,00`, todo ASCII — y el
 *   rotulo sigue saliendo del dato, que es lo que pide la regla de #617.
 * @returns {string} Formatted currency string
 */
let avisoSinMonedaEmitido = false;

export const formatCurrency = (amount, currency, locale = 'es-CR', options = {}) => {
  if (amount === null || amount === undefined) return '-';

  const { currencyDisplay } = options;

  // Sin dato de moneda -> numero pelado. La rama va ANTES de construir el
  // Intl.NumberFormat: con `style: 'currency'` y `currency: undefined` el
  // constructor tira, y el catch de abajo escupiria "undefined 1234,00".
  if (!currency) {
    // Una sola vez por sesion: sin dedupe esto grita en cada render de cada
    // fila de cada tabla de montos y el aviso se vuelve ruido que se ignora.
    if (process.env.NODE_ENV === 'development' && !avisoSinMonedaEmitido) {
      avisoSinMonedaEmitido = true;
      console.warn(
        '[formatCurrency] hay llamadas sin moneda: esos montos se muestran SIN ' +
        'simbolo (#617). Es lo esperado mientras la entidad no tenga columna ' +
        '`currency`; si la tiene (invoices, purchases, inventory_batches), pasala.'
      );
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      console.error('Currency formatting error:', error);
      return Number(amount).toFixed(2);
    }
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...(currencyDisplay && { currencyDisplay }),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    // Codigo invalido (p.ej. la cadena `'USD'` CON comillas que dejaba el
    // DEFAULT mal escapado de `invoices.currency` antes de #558). Se muestra
    // crudo: sigue saliendo del dato, no de un supuesto nuestro.
    console.error('Currency formatting error:', error);
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
};

/**
 * Format number with thousands separator
 * @param {number} number - Number to format
 * @param {number} decimals - Number of decimal places
 * @param {string} locale - Locale string (default 'es-CR')
 * @returns {string} Formatted number string
 */
export const formatNumber = (number, decimals = 0, locale = 'es-CR') => {
  if (number === null || number === undefined) return '-';

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number);
  } catch (error) {
    console.error('Number formatting error:', error);
    return number.toFixed(decimals);
  }
};

/**
 * Format percentage
 * @param {number} value - Value to format as percentage
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  return `${formatNumber(value, decimals)}%`;
};

/**
 * Format file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format project status for display
 * @param {string} status - Project status
 * @returns {Object} Status display info
 */
export const formatProjectStatus = (status) => {
  const statusMap = {
    draft: { label: 'Borrador', variant: 'secondary', icon: 'bi-file-earmark' },
    quoted: { label: 'Cotizado', variant: 'warning', icon: 'bi-file-text' },
    quotation_sent: { label: 'Cotizacion Enviada', variant: 'info', icon: 'bi-send' },
    quotation_accepted: { label: 'Cotizacion Aceptada', variant: 'success', icon: 'bi-check2-circle' },
    po_received: { label: 'PO Recibido', variant: 'info', icon: 'bi-file-check' },
    active: { label: 'Activo', variant: 'success', icon: 'bi-play-circle' },
    closed: { label: 'Cerrado', variant: 'dark', icon: 'bi-check-circle' },
  };

  return statusMap[status] || { label: status, variant: 'secondary', icon: 'bi-question-circle' };
};

/**
 * Format quotation status for display
 * @param {string} status - Quotation status
 * @returns {Object} Status display info
 */
export const formatQuotationStatus = (status) => {
  const statusMap = {
    draft: { label: 'Borrador', variant: 'secondary' },
    under_review: { label: 'En Revisión', variant: 'info' },
    approved: { label: 'Aprobada', variant: 'success' },
    sent: { label: 'Enviada', variant: 'primary' },
    accepted: { label: 'Aceptada', variant: 'success' },
    rejected: { label: 'Rechazada', variant: 'danger' },
  };

  return statusMap[status] || { label: status, variant: 'secondary' };
};

/**
 * Format expense category for display
 * @param {string} category - Expense category
 * @returns {string} Formatted category
 */
export const formatExpenseCategory = (category) => {
  const categoryMap = {
    materials: 'Materiales',
    tools: 'Herramientas',
    labor: 'Mano de Obra',
    transportation: 'Transporte',
    other: 'Otros',
  };

  return categoryMap[category] || category;
};

/**
 * Format user role for display
 * @param {string} role - User role
 * @returns {string} Formatted role
 */
export const formatUserRole = (role) => {
  const roleMap = {
    admin: 'Administrador',
    supervisor: 'Supervisor',
    technician: 'Tecnico',
    operario: 'Operario',
    mensajero: 'Mensajero',
    client: 'Cliente',
  };

  return roleMap[role] || role;
};

/**
 * Format task status for display
 * @param {string} status - Task status
 * @returns {Object} Status display info
 */
export const formatTaskStatus = (status) => {
  // Los seis valores de `tasks_status_check`, ni uno menos. El mapa anterior
  // tenia `todo` --que el CHECK no admite-- y le faltaban `pending` y
  // `cancelled`, asi que la cabecera del detalle mostraba la etiqueta cruda
  // "pending" en las 177 tareas pendientes de produccion.
  const statusMap = {
    pending: { label: 'Pendiente', variant: 'secondary', icon: 'bi-circle' },
    in_progress: { label: 'En Progreso', variant: 'primary', icon: 'bi-play-circle' },
    blocked: { label: 'Bloqueada', variant: 'danger', icon: 'bi-exclamation-circle' },
    review: { label: 'En Revisión', variant: 'warning', icon: 'bi-eye' },
    completed: { label: 'Completada', variant: 'success', icon: 'bi-check-circle' },
    cancelled: { label: 'Cancelada', variant: 'dark', icon: 'bi-x-circle' },
  };

  return statusMap[status] || { label: status, variant: 'secondary', icon: 'bi-question-circle' };
};

/**
 * Format task priority for display
 * @param {string} priority - Task priority
 * @returns {Object} Priority display info
 */
export const formatTaskPriority = (priority) => {
  const priorityMap = {
    low: { label: 'Baja', variant: 'secondary', icon: 'bi-arrow-down' },
    medium: { label: 'Media', variant: 'warning', icon: 'bi-dash' },
    high: { label: 'Alta', variant: 'danger', icon: 'bi-arrow-up' },
    urgent: { label: 'Urgente', variant: 'danger', icon: 'bi-exclamation-triangle' },
  };

  return priorityMap[priority] || { label: priority, variant: 'secondary', icon: 'bi-question-circle' };
};

const formatters = {
  parseDateOnly,
  isOverdue,
  formatDate,
  formatDateShort,
  formatDateTime,
  formatRelativeAge,
  formatTime,
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatFileSize,
  truncateText,
  formatProjectStatus,
  formatQuotationStatus,
  formatExpenseCategory,
  formatUserRole,
  formatTaskStatus,
  formatTaskPriority,
};

export default formatters;
